import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { $getSelection, $isRangeSelection, $setSelection, COMMAND_PRIORITY_CRITICAL, SELECTION_CHANGE_COMMAND, getDOMSelection, isDOMNode } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalEditable } from "@lexical/react/useLexicalEditable";
import { $deleteTableColumnAtSelection, $deleteTableRowAtSelection, $getNodeTriplet, $getTableCellNodeFromLexicalNode, $getTableNodeFromLexicalNodeOrThrow, $insertTableColumnAtSelection, $insertTableRowAtSelection, $isTableCellNode, $isTableNode, $isTableSelection, TableCellNode, getTableElement, getTableObserverFromTableElement } from "@lexical/table";
import { mergeRegister } from "@lexical/utils";
import { createPortal } from "react-dom";
import { Box } from "../../../../Box/index.js";
import { Icon } from "../../../../Icon/index.js";
import { Typography } from "../../../../Typography/index.js";
import { MenuItem } from "../../../../MenuItem/index.js";
import { Popover } from "../../../../Popover/index.js";
import { IconButton } from "../../../../IconButton/index.js";
import { getSelectedNode } from "../../utils/index.js";
import { useDropdownButtonPosition } from "./hooks/index.js";
const getTableDimensions = (tableNode)=>{
    const columns = tableNode.getColumnCount();
    const rows = tableNode.getChildren().length;
    return {
        rows: rows,
        columns: columns
    };
};
const currentCellBackgroundColor = (editor)=>editor.getEditorState().read(()=>{
        const selection = $getSelection();
        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
            const [cell] = $getNodeTriplet(selection.anchor);
            if ($isTableCellNode(cell)) return cell.getBackgroundColor();
        }
        return null;
    });
const findTableNodeFromNode = (startNode)=>{
    let currentNode = startNode;
    while(currentNode){
        if ($isTableNode(currentNode)) return currentNode;
        currentNode = currentNode.getParent();
    }
    return null;
};
const TableActionMenu = ({ onClose, tableCellNode: _tableCellNode, setIsMenuOpen, contextRef })=>{
    const [editor] = useLexicalComposerContext();
    const theme = useTheme();
    const dropDownRef = useRef(null);
    const [tableCellNode, updateTableCellNode] = useState(_tableCellNode);
    const [tableDimensions, setTableDimensions] = useState({
        columns: 1,
        rows: 1
    });
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(contextRef.current);
    const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState(null);
    const actionMenuOpen = Boolean(actionMenuAnchorEl);
    const colorPickerOpen = Boolean(colorPickerAnchorEl);
    const isRowsInsertDisabled = tableDimensions.rows >= 10;
    const isColumnsInsertDisabled = tableDimensions.columns >= 10;
    const ColorTypeToBlockName = {
        [theme.tokens.colors.bg.light.primary]: 'Белый',
        [theme.tokens.colors.stroke.primary]: 'Серый',
        [theme.tokens.colors.accent.stroke.blue]: 'Синий',
        [theme.tokens.colors.accent.stroke.success]: 'Зеленый',
        [theme.tokens.colors.accent.stroke.warning]: 'Оранжевый',
        [theme.tokens.colors.accent.stroke.error]: 'Красный',
        [theme.tokens.colors.accent.stroke.review]: 'Фиолетовый'
    };
    const [backgroundColor, setBackgroundColor] = useState(()=>currentCellBackgroundColor(editor) || '');
    useEffect(()=>editor.registerMutationListener(TableCellNode, (nodeMutations)=>{
            const nodeUpdated = 'updated' === nodeMutations.get(tableCellNode.getKey());
            if (nodeUpdated) {
                editor.getEditorState().read(()=>{
                    updateTableCellNode(tableCellNode.getLatest());
                });
                setBackgroundColor(currentCellBackgroundColor(editor) || '');
            }
        }, {
            skipInitialization: true
        }), [
        editor,
        tableCellNode
    ]);
    useEffect(()=>{
        editor.getEditorState().read(()=>{
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const selectedNode = getSelectedNode(selection);
                const tableNode = findTableNodeFromNode(selectedNode);
                if (tableNode) setTableDimensions(getTableDimensions(tableNode));
            }
        });
    }, [
        editor
    ]);
    useDropdownButtonPosition({
        contextRef,
        dropDownRef,
        editor
    });
    useEffect(()=>{
        const handleClickOutside = (event)=>{
            if (null != dropDownRef.current && null != contextRef.current && isDOMNode(event.target) && !dropDownRef.current.contains(event.target) && !contextRef.current.contains(event.target)) {
                setIsMenuOpen(false);
                setActionMenuAnchorEl(null);
                setColorPickerAnchorEl(null);
            }
        };
        window.addEventListener('click', handleClickOutside);
        return ()=>window.removeEventListener('click', handleClickOutside);
    }, [
        setIsMenuOpen,
        contextRef
    ]);
    const clearTableSelection = useCallback(()=>{
        editor.update(()=>{
            if (tableCellNode.isAttached()) {
                const tableNode_0 = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
                const tableElement = getTableElement(tableNode_0, editor.getElementByKey(tableNode_0.getKey()));
                if (null === tableElement) throw new Error('Не удалось найти таблицу');
                const tableObserver = getTableObserverFromTableElement(tableElement);
                if (null !== tableObserver) tableObserver.$clearHighlight();
                tableNode_0.markDirty();
                updateTableCellNode(tableCellNode.getLatest());
            }
            $setSelection(null);
        });
    }, [
        editor,
        tableCellNode
    ]);
    const insertTableRowAtSelection = useCallback((shouldInsertAfter)=>{
        editor.update(()=>{
            $insertTableRowAtSelection(shouldInsertAfter);
            onClose();
        });
    }, [
        editor,
        onClose
    ]);
    const insertTableColumnAtSelection = useCallback((shouldInsertAfter_0)=>{
        editor.update(()=>{
            $insertTableColumnAtSelection(shouldInsertAfter_0);
            onClose();
        });
    }, [
        editor,
        onClose
    ]);
    const deleteTableRowAtSelection = useCallback(()=>{
        editor.update(()=>{
            $deleteTableRowAtSelection();
            onClose();
        });
    }, [
        editor,
        onClose
    ]);
    const deleteTableAtSelection = useCallback(()=>{
        editor.update(()=>{
            const tableNode_1 = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
            tableNode_1.remove();
            clearTableSelection();
            onClose();
        });
    }, [
        editor,
        tableCellNode,
        clearTableSelection,
        onClose
    ]);
    const deleteTableColumnAtSelection = useCallback(()=>{
        editor.update(()=>{
            $deleteTableColumnAtSelection();
            onClose();
        });
    }, [
        editor,
        onClose
    ]);
    const handleColorPickerChange = (e)=>{
        if (colorPickerAnchorEl) return;
        setColorPickerAnchorEl(()=>e.currentTarget);
    };
    const handleCellBackgroundColor = useCallback((color)=>{
        editor.update(()=>{
            const selection_0 = $getSelection();
            if ($isRangeSelection(selection_0) || $isTableSelection(selection_0)) {
                const [cell] = $getNodeTriplet(selection_0.anchor);
                if ($isTableCellNode(cell)) cell.setBackgroundColor(color);
                if ($isTableSelection(selection_0)) {
                    const nodes = selection_0.getNodes();
                    for(let i = 0; i < nodes.length; i++){
                        const node = nodes[i];
                        if ($isTableCellNode(node)) node.setBackgroundColor(color);
                    }
                }
            }
        });
    }, [
        editor
    ]);
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ jsx(Popover, {
        open: actionMenuOpen,
        anchorEl: actionMenuAnchorEl,
        onClose: ()=>{
            setActionMenuAnchorEl(null);
        },
        onClick: (e_0)=>{
            e_0.stopPropagation();
        },
        ref: dropDownRef,
        anchorOrigin: {
            vertical: 'top',
            horizontal: 'right'
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
        },
        children: /*#__PURE__*/ jsxs(Box, {
            paddingY: "8",
            children: [
                /*#__PURE__*/ jsx(Box, {
                    onClick: handleColorPickerChange,
                    width: "100%",
                    children: /*#__PURE__*/ jsx(MenuItem, {
                        children: /*#__PURE__*/ jsxs(Box, {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            children: [
                                /*#__PURE__*/ jsx(Typography, {
                                    variant: "body2",
                                    color: backgroundColor ? 'original.brand.primary' : 'text.light.primary',
                                    children: "Цвет фона"
                                }),
                                /*#__PURE__*/ jsx(Icon, {
                                    name: "chevron_right",
                                    size: "12"
                                })
                            ]
                        })
                    })
                }),
                /*#__PURE__*/ jsx(Popover, {
                    open: colorPickerOpen,
                    anchorEl: colorPickerAnchorEl,
                    onClose: ()=>{
                        setColorPickerAnchorEl(null);
                    },
                    slotProps: {
                        paper: {
                            style: {
                                marginLeft: '4px'
                            }
                        }
                    },
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right'
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                    },
                    children: Object.entries(ColorTypeToBlockName).map(([color_0, name])=>/*#__PURE__*/ jsx(MenuItem, {
                            onClick: ()=>handleCellBackgroundColor(color_0),
                            children: /*#__PURE__*/ jsxs(Box, {
                                display: "flex",
                                alignItems: "center",
                                gap: "8",
                                children: [
                                    /*#__PURE__*/ jsx(Box, {
                                        width: "16px",
                                        height: "16px",
                                        radius: "xs",
                                        style: {
                                            backgroundColor: color_0,
                                            border: `1px solid ${color_0 === theme.tokens.colors.bg.light.primary ? theme.tokens.colors.stroke.primary : 'transparent'}`
                                        },
                                        border: "1px solid"
                                    }),
                                    /*#__PURE__*/ jsx(Typography, {
                                        variant: "body3",
                                        color: backgroundColor === color_0 ? 'original.brand.primary' : 'text.light.primary',
                                        children: name
                                    })
                                ]
                            })
                        }, color_0))
                }),
                /*#__PURE__*/ jsx(Box, {
                    borderBottom: "1px solid",
                    borderColor: "stroke.primary"
                }),
                /*#__PURE__*/ jsx(MenuItem, {
                    disabled: isRowsInsertDisabled,
                    onClick: ()=>insertTableRowAtSelection(false),
                    children: /*#__PURE__*/ jsx(Typography, {
                        variant: "body2",
                        children: "Вставить строку выше"
                    })
                }),
                /*#__PURE__*/ jsx(MenuItem, {
                    disabled: isRowsInsertDisabled,
                    onClick: ()=>insertTableRowAtSelection(true),
                    children: /*#__PURE__*/ jsx(Typography, {
                        variant: "body2",
                        children: "Вставить строку ниже"
                    })
                }),
                /*#__PURE__*/ jsx(Box, {
                    borderBottom: "1px solid",
                    borderColor: "stroke.primary"
                }),
                /*#__PURE__*/ jsx(MenuItem, {
                    disabled: isColumnsInsertDisabled,
                    onClick: ()=>insertTableColumnAtSelection(false),
                    children: /*#__PURE__*/ jsx(Typography, {
                        variant: "body2",
                        children: "Вставить столбец слева"
                    })
                }),
                /*#__PURE__*/ jsx(MenuItem, {
                    disabled: isColumnsInsertDisabled,
                    onClick: ()=>insertTableColumnAtSelection(true),
                    children: /*#__PURE__*/ jsx(Typography, {
                        variant: "body2",
                        children: "Вставить столбец справа"
                    })
                }),
                /*#__PURE__*/ jsx(Box, {
                    borderBottom: "1px solid",
                    borderColor: "stroke.primary"
                }),
                /*#__PURE__*/ jsx(MenuItem, {
                    onClick: ()=>deleteTableColumnAtSelection(),
                    children: /*#__PURE__*/ jsx(Typography, {
                        variant: "body2",
                        children: "Удалить столбец"
                    })
                }),
                /*#__PURE__*/ jsx(MenuItem, {
                    onClick: ()=>deleteTableRowAtSelection(),
                    children: /*#__PURE__*/ jsx(Typography, {
                        variant: "body2",
                        children: "Удалить строку"
                    })
                }),
                /*#__PURE__*/ jsx(MenuItem, {
                    onClick: ()=>deleteTableAtSelection(),
                    children: /*#__PURE__*/ jsx(Typography, {
                        variant: "body2",
                        children: "Удалить таблицу"
                    })
                })
            ]
        })
    }), document.body);
};
const TableCellActionMenuContainer = (t0)=>{
    const $ = c(14);
    const { anchorElem } = t0;
    const [editor] = useLexicalComposerContext();
    const menuButtonRef = useRef(null);
    const menuRootRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [tableCellNode, setTableMenuCellNode] = useState(null);
    let t1;
    if ($[0] !== anchorElem || $[1] !== editor) {
        t1 = ()=>{
            const menu = menuButtonRef.current;
            const selection = $getSelection();
            const nativeSelection = getDOMSelection(editor._window);
            const activeElement = document.activeElement;
            const disable = ()=>{
                setTableMenuCellNode(null);
            };
            if (null == selection || null == menu) return disable();
            const rootElement = editor.getRootElement();
            let tableObserver = null;
            let tableCellParentNodeDOM = null;
            if ($isRangeSelection(selection) && null !== rootElement && null !== nativeSelection && rootElement.contains(nativeSelection.anchorNode)) {
                const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(selection.anchor.getNode());
                if (null == tableCellNodeFromSelection) return disable();
                tableCellParentNodeDOM = editor.getElementByKey(tableCellNodeFromSelection.getKey());
                if (null == tableCellParentNodeDOM || !tableCellNodeFromSelection.isAttached()) return disable();
                const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNodeFromSelection);
                const tableElement = getTableElement(tableNode, editor.getElementByKey(tableNode.getKey()));
                if (null === tableElement) throw new Error("\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043D\u0430\u0439\u0442\u0438 \u0442\u0430\u0431\u043B\u0438\u0446\u0443");
                tableObserver = getTableObserverFromTableElement(tableElement);
                setTableMenuCellNode(tableCellNodeFromSelection);
            } else if ($isTableSelection(selection)) {
                const anchorNode = $getTableCellNodeFromLexicalNode(selection.anchor.getNode());
                if (!$isTableCellNode(anchorNode)) throw new Error("\u042F\u043A\u043E\u0440\u044C anchorNode \u0434\u043E\u043B\u0436\u0435\u043D \u0431\u044B\u0442\u044C \u043D\u043E\u0434\u043E\u0439 TableCellNode");
                const tableNode_0 = $getTableNodeFromLexicalNodeOrThrow(anchorNode);
                const tableElement_0 = getTableElement(tableNode_0, editor.getElementByKey(tableNode_0.getKey()));
                if (null === tableElement_0) throw new Error("\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043D\u0430\u0439\u0442\u0438 \u0442\u0430\u0431\u043B\u0438\u0446\u0443");
                tableObserver = getTableObserverFromTableElement(tableElement_0);
                tableCellParentNodeDOM = editor.getElementByKey(anchorNode.getKey());
                if (null === tableCellParentNodeDOM) return disable();
            } else if (!activeElement) return disable();
            if (null === tableObserver || null === tableCellParentNodeDOM) return disable();
            const enabled = !tableObserver || !tableObserver.isSelecting;
            if (enabled) {
                const tableCellRect = tableCellParentNodeDOM.getBoundingClientRect();
                const anchorRect = anchorElem.getBoundingClientRect();
                const top = tableCellRect.top - anchorRect.top;
                const left = tableCellRect.right - anchorRect.left;
                menu.style.transform = `translate(${left}px, ${top}px)`;
            }
        };
        $[0] = anchorElem;
        $[1] = editor;
        $[2] = t1;
    } else t1 = $[2];
    const $moveMenu = t1;
    let t2;
    if ($[3] !== $moveMenu || $[4] !== editor) {
        t2 = ()=>{
            let timeoutId;
            const callback = ()=>{
                timeoutId = void 0;
                editor.getEditorState().read($moveMenu);
            };
            const delayedCallback = ()=>{
                if (void 0 === timeoutId) timeoutId = setTimeout(callback, 0);
                return false;
            };
            return mergeRegister(editor.registerUpdateListener(delayedCallback), editor.registerCommand(SELECTION_CHANGE_COMMAND, delayedCallback, COMMAND_PRIORITY_CRITICAL), editor.registerRootListener((rootElement_0, prevRootElement)=>{
                if (prevRootElement) prevRootElement.removeEventListener("pointerup", delayedCallback);
                if (rootElement_0) {
                    rootElement_0.addEventListener("pointerup", delayedCallback);
                    delayedCallback();
                }
            }), ()=>clearTimeout(timeoutId));
        };
        $[3] = $moveMenu;
        $[4] = editor;
        $[5] = t2;
    } else t2 = $[5];
    useEffect(t2);
    const prevTableCellDOM = useRef(tableCellNode);
    let t3;
    let t4;
    if ($[6] !== tableCellNode) {
        t3 = ()=>{
            if (prevTableCellDOM.current !== tableCellNode) setIsMenuOpen(false);
            prevTableCellDOM.current = tableCellNode;
        };
        t4 = [
            prevTableCellDOM,
            tableCellNode
        ];
        $[6] = tableCellNode;
        $[7] = t3;
        $[8] = t4;
    } else {
        t3 = $[7];
        t4 = $[8];
    }
    useEffect(t3, t4);
    let t5;
    if ($[9] !== isMenuOpen || $[10] !== tableCellNode) {
        t5 = null != tableCellNode && /*#__PURE__*/ jsxs(Fragment, {
            children: [
                /*#__PURE__*/ jsx(Box, {
                    onClick: (e)=>{
                        e.stopPropagation();
                        setIsMenuOpen(!isMenuOpen);
                    },
                    ref: menuRootRef,
                    position: "absolute",
                    right: "0",
                    top: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "24px",
                    height: "24px",
                    bgcolor: "bg.greyscale.primary",
                    radius: "s",
                    marginRight: "12",
                    marginTop: "16",
                    children: /*#__PURE__*/ jsx(IconButton, {
                        size: "small",
                        children: /*#__PURE__*/ jsx(Icon, {
                            name: "chevron_down",
                            size: "16"
                        })
                    })
                }),
                isMenuOpen && /*#__PURE__*/ jsx(TableActionMenu, {
                    contextRef: menuRootRef,
                    setIsMenuOpen: setIsMenuOpen,
                    onClose: ()=>setIsMenuOpen(false),
                    tableCellNode: tableCellNode
                })
            ]
        });
        $[9] = isMenuOpen;
        $[10] = tableCellNode;
        $[11] = t5;
    } else t5 = $[11];
    let t6;
    if ($[12] !== t5) {
        t6 = /*#__PURE__*/ jsx(Box, {
            position: "absolute",
            zIndex: "3",
            top: "0",
            left: "0",
            ref: menuButtonRef,
            children: t5
        });
        $[12] = t5;
        $[13] = t6;
    } else t6 = $[13];
    return t6;
};
const TableActionMenuPlugin = (t0)=>{
    const $ = c(6);
    const { anchorElem: t1 } = t0;
    const anchorElem = void 0 === t1 ? document.body : t1;
    const isEditable = useLexicalEditable();
    let t2;
    if ($[0] !== anchorElem || $[1] !== isEditable) {
        t2 = isEditable ? /*#__PURE__*/ jsx(TableCellActionMenuContainer, {
            anchorElem: anchorElem
        }) : null;
        $[0] = anchorElem;
        $[1] = isEditable;
        $[2] = t2;
    } else t2 = $[2];
    let t3;
    if ($[3] !== anchorElem || $[4] !== t2) {
        t3 = /*#__PURE__*/ createPortal(t2, anchorElem);
        $[3] = anchorElem;
        $[4] = t2;
        $[5] = t3;
    } else t3 = $[5];
    return t3;
};
export { TableActionMenuPlugin };
